/**
 * ShellOrderRail - the order strip above the map, and the acting combatant's turn card over it (plan design-gaps
 * L2; the column beside the map, with the card under a row, went with the classic layout in v12.0.0).
 *
 * THE RAIL YIELDS TO BUILD AND DEPLOY (v8.318.0), as the log does: the map
 * wants the column, and the rail is about the fight, not the furniture -
 * so `orderRailNode` is null then, exactly as the log panel expects.
 * THE TURN CARD (v8.322.0) is the turn as it stands, projected from state
 * the shell already holds; every door on it is one of the shell's writers.
 * Extracted from DeathWatchSimulator; the markup is unchanged.
 */
import OrderRail from '../OrderRail';
import TurnCard from '../TurnCard';
import { actingRow } from '../../engine/roster/orderRail';

/** The acting combatant's turn card. */
function ShellTurnCard({ card, turnTracker, heading }) {
  const { activeTab, impactsNow, ledgerData } = card;
  return (
    <TurnCard
      heading={heading}
      intent={card.intent}
      weapons={card.currentSlots.map((w, index) => ({ index, name: w.weaponName, isMelee: w.weaponType === 'melee' }))}
      selectedSlot={card.selectedSlot}
      onArm={card.setSelectedSlot}
      onAttack={card.handleAttackOrChambered}
      onEndTurn={turnTracker.trackerState.active ? turnTracker.endTurn : null}
      endSuggested={card.endSuggested}
      impact={activeTab ? (impactsNow[activeTab] ?? null) : null}
      onRemoveChip={(id) => ledgerData.chips.find((c) => c.id === id)?.onRemove?.()}
    />
  );
}

/** The strip itself. */
function ShellOrderRail({ rail, endSuggested }) {
  const { turnTracker, isLocked, untrackedRows } = rail;
  return (
    <OrderRail
      rows={rail.railRows}
      trackerState={turnTracker.trackerState}
      locked={isLocked('turnTracker')}
      onEndTurn={turnTracker.endTurn}
      onTogglePause={turnTracker.togglePause}
      onSetInitiative={turnTracker.setParticipantInitiative}
      onRemoveParticipant={turnTracker.removeParticipant}
      onReorderParticipants={turnTracker.reorderParticipants}
      onAddCustom={turnTracker.addCustomParticipant}
      onTrackEveryone={untrackedRows.length ? rail.handleTrackEveryone : null}
      onResetCombat={turnTracker.resetCombat}
      onReroll={turnTracker.rerollInitiative}
      onRerollAll={turnTracker.rerollAll}
      onRerollOthers={turnTracker.rerollOthers}
      onDismissJoinHint={turnTracker.dismissJoinHint}
      onSetOrderMode={turnTracker.setOrderMode}
      showDialog={rail.showDialogAsync}
      hoveredId={rail.hoveredId}
      onHoverRow={rail.setHoveredId}
      endSuggested={endSuggested}
    />
  );
}

/**
 * The log panel's rail node: null while the map wants the column.
 *
 * @param {boolean} mapWantsColumn - Build or Deploy is open
 * @param {object} rail - railRows, turnTracker, isLocked, untrackedRows,
 *   handleTrackEveryone, showDialogAsync, hoveredId, setHoveredId
 * @param {boolean} endSuggested - my actor's pips are spent: END TURN lights (turnCardArgs)
 */
export function orderRailNode(mapWantsColumn, rail, endSuggested) {
  return mapWantsColumn ? null : <ShellOrderRail rail={rail} endSuggested={endSuggested} />;
}

/** "ROUND 2 · CASTIEL ACTS": who acts now, by the tracker; nothing while it is off. */
function turnHeading(rail) {
  const { trackerState } = rail.turnTracker;
  const acting = trackerState.active ? actingRow(rail.railRows) : null;
  return acting ? `ROUND ${trackerState.round} · ${acting.name.toUpperCase()} ACTS` : null;
}

/**
 * The turn card over the map (the strip has no room for one; the overlay
 * places it, plan design-gaps L2). Only while the viewer acts as someone;
 * yields with the strip.
 */
export function turnCardNode(mapWantsColumn, rail, card) {
  if (mapWantsColumn || !card.actor) return null;
  return <ShellTurnCard card={card} turnTracker={rail.turnTracker} heading={turnHeading(rail)} />;
}
